import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import type { WorkOrderPriceItem } from 'gql/graphql';
import { ContractStatusEnum, ProjectStatusEnum, WorkOrderPriceTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useDropdownContextMenuTooltipController } from 'hooks/useDropdownContextMenuTooltipController';
import { useProjectData } from 'hooks/useProjectData';
import { WorkOrderOrChangeObjectTotals } from 'pages/Project/UerProject/ObjectTotals/WorkOrderOrChangeObjectTotals';
import { Button, Checkbox, ContextMenu, IconExpand, IconKebab, Loader, Tooltip } from 'uikit';

import type { ICellRendererParams } from 'ag-grid-community/dist/types/core/rendering/cellRenderers/iCellRenderer';
import * as R from 'ramda';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { WorkOrderMenuActionTypes } from '../types';

import { DeletableWorkOrderItemsStatus, useCheckDeletableWorkOrder } from './hooks/useCheckDeletableWorkOrder';
import { useMenuItemsByRowType } from './hooks/useMenuItemsByRowType';

type WorkOrderMenuCellRendererProps = ICellRendererParams<WorkOrderPriceItem> & {
    workOrderStatus: WorkOrderStatusEnum;
    openModalHandler: (type: WorkOrderMenuActionTypes, rowData: WorkOrderPriceItem, options?: any) => void;
    checkedRows: Record<string, WorkOrderPriceItem>;
    checkRowHandler: ({ id, data }: { id: string; data: WorkOrderPriceItem | null | undefined }) => void;
};
const getItemLabel = R.prop('label');

export const WorkOrderMenuCellRenderer = (props: WorkOrderMenuCellRendererProps) => {
    const { data, workOrderStatus, openModalHandler, checkRowHandler, checkedRows } = props;
    const { aktKC2: aktKC2Rules, order: orderRules } = useAccess();

    const ref = useRef(null);
    const { id: projectId } = useParams();
    const { projectData } = useProjectData(projectId, false);

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isObjectTotalsOpen, setIsObjectTotalsOpen] = useState<boolean>(false);

    const onSwitchObjectTotalsVisibility = useCallback(() => {
        setIsObjectTotalsOpen(R.not);
    }, []);

    const handleOnClick = useCallback(() => {
        setIsOpen(R.not);
    }, []);

    const canCreateAktKC2 = useMemo(() => aktKC2Rules?.canCreate, [aktKC2Rules?.canCreate]);

    const isDisabledByProjectStatus = useMemo(
        () => projectData?.status === ProjectStatusEnum.Delayed || projectData?.status === ProjectStatusEnum.Completed,
        [projectData?.status],
    );

    const workOrderOnFormation = useMemo(() => workOrderStatus === WorkOrderStatusEnum.OnFormation, [workOrderStatus]);
    const workOrderInProgress = useMemo(() => workOrderStatus === WorkOrderStatusEnum.InProgress, [workOrderStatus]);

    const isDisabledByContractStatus = useMemo(
        () => projectData?.contractNavigation?.data?.contractNavData?.status !== ContractStatusEnum.InImplementation,
        [projectData?.contractNavigation?.data?.contractNavData?.status],
    );

    const isAddActsBtnDisabled = useMemo(
        () => isDisabledByContractStatus || !canCreateAktKC2 || isDisabledByProjectStatus,
        [canCreateAktKC2, isDisabledByContractStatus, isDisabledByProjectStatus],
    );
    const isItemsMenuBtnDisabled = useMemo(() => {
        if (data?.type !== WorkOrderPriceTypeEnum.Other) {
            return Boolean(
                (!workOrderOnFormation && !workOrderInProgress) ||
                    isDisabledByContractStatus ||
                    isDisabledByProjectStatus ||
                    (workOrderInProgress && !data?.hasOngoingSmrActs && !data?.hasOngoingSkzsActs) ||
                    data?.isExcludedPrice ||
                    !orderRules?.canEdit,
            );
        }

        return false;
    }, [
        data?.hasOngoingSkzsActs,
        data?.hasOngoingSmrActs,
        data?.isExcludedPrice,
        data?.type,
        isDisabledByContractStatus,
        isDisabledByProjectStatus,
        orderRules?.canEdit,
        workOrderInProgress,
        workOrderOnFormation,
    ]);

    const deletableItems = useCheckDeletableWorkOrder(data, workOrderStatus);
    const messages = deletableItems?.messages?.[data?.type as WorkOrderPriceTypeEnum];
    const showMessage = messages?.itemsIds?.includes(data?.itemId);

    const isDeleteDisabled = useMemo(
        () =>
            deletableItems?.status === DeletableWorkOrderItemsStatus.noDeletable ||
            (deletableItems?.status === DeletableWorkOrderItemsStatus.partlyDeletable &&
                !deletableItems?.deletableIds?.includes(data?.itemId)) ||
            isDisabledByContractStatus ||
            isDisabledByProjectStatus,
        [
            data?.itemId,
            deletableItems?.deletableIds,
            deletableItems?.status,
            isDisabledByContractStatus,
            isDisabledByProjectStatus,
        ],
    );

    const handleItemOnClick = useCallback(
        (item: (typeof menuItems)[number]) => () => {
            item?.action?.();
        },
        [],
    );

    const { menuItems, menuGroups } = useMenuItemsByRowType({
        rowData: data,
        openModalHandler,
        workOrderStatus,
        isDeleteDisabled,
    });
    const { getMenuItemAttributes, tooltipPosition, activeTooltipMsg, onMenuMouseMove, onMenuMouseLeave } =
        useDropdownContextMenuTooltipController();

    const handleCheckbox = useCallback(
        (id: string) =>
            ({ checked }: { checked: boolean }) => {
                if (checked) {
                    checkRowHandler({ id, data });
                } else {
                    checkRowHandler({ id, data: null });
                }
            },
        [checkRowHandler, data],
    );

    if (!data) {
        return <Loader />;
    }

    if (data?.type === WorkOrderPriceTypeEnum.OtherGroup) {
        return null;
    }

    if (data?.type === WorkOrderPriceTypeEnum.RootObject) {
        return (
            <>
                <Button
                    iconLeft={IconExpand}
                    onlyIcon
                    view="clear"
                    size="xs"
                    onClick={onSwitchObjectTotalsVisibility}
                />
                {workOrderInProgress && (
                    <>
                        <Button
                            ref={ref}
                            iconLeft={IconKebab}
                            onlyIcon
                            view="clear"
                            size="xs"
                            onClick={handleOnClick}
                            disabled={isAddActsBtnDisabled}
                        />
                        {Boolean(menuItems?.length) && (
                            <ContextMenu
                                isOpen={isOpen}
                                items={menuItems}
                                groups={menuGroups}
                                getItemLabel={getItemLabel}
                                anchorRef={ref}
                                direction="downRight"
                                size="s"
                                getItemOnClick={handleItemOnClick}
                                onClickOutside={handleOnClick}
                                getItemAttributes={getMenuItemAttributes}
                                onMouseLeave={onMenuMouseLeave}
                                onMouseMove={onMenuMouseMove}
                            />
                        )}
                    </>
                )}
                {isObjectTotalsOpen && (
                    <WorkOrderOrChangeObjectTotals
                        onClose={onSwitchObjectTotalsVisibility}
                        rowData={data}
                        costRounding={
                            projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding ?? 0
                        }
                    />
                )}
                {activeTooltipMsg && (
                    <Tooltip
                        size="s"
                        direction="rightCenter"
                        position={tooltipPosition}
                        isInteractive={false}
                        style={{ zIndex: 2 }}
                    >
                        {activeTooltipMsg}
                    </Tooltip>
                )}
            </>
        );
    }

    return (
        <>
            <ComponentWithTooltip tooltipText={showMessage ? messages?.message : ''}>
                <Checkbox
                    checked={!!checkedRows[data.clientId]}
                    onChange={handleCheckbox(data.clientId)}
                    disabled={isDeleteDisabled}
                />
            </ComponentWithTooltip>

            <Button
                ref={ref}
                iconLeft={IconKebab}
                onlyIcon
                view="clear"
                size="xs"
                onClick={handleOnClick}
                disabled={!menuItems?.length || isItemsMenuBtnDisabled}
                style={{
                    marginLeft: '6px',
                }}
            />
            {Boolean(menuItems?.length) && (
                <ContextMenu
                    isOpen={isOpen}
                    items={menuItems}
                    groups={menuGroups}
                    getItemLabel={getItemLabel}
                    anchorRef={ref}
                    direction="downRight"
                    size="s"
                    getItemOnClick={handleItemOnClick}
                    onClickOutside={handleOnClick}
                    getItemAttributes={getMenuItemAttributes}
                    onMouseLeave={onMenuMouseLeave}
                    onMouseMove={onMenuMouseMove}
                />
            )}
            {activeTooltipMsg && (
                <Tooltip
                    size="s"
                    direction="rightCenter"
                    position={tooltipPosition}
                    isInteractive={false}
                    style={{ zIndex: 2 }}
                >
                    {activeTooltipMsg}
                </Tooltip>
            )}
        </>
    );
};

WorkOrderMenuCellRenderer.displayName = 'WorkOrderMenuCellRenderer';
